// "Your journey" — a timeline of when each part of the portrait actually appeared.
//
// The design shows three written moments, the last marked NOW. We don't have written moments: we
// have timestamps. Rather than ask a model to narrate a story about someone's year — which is
// exactly the kind of invention this screen exists to avoid — each step states a fact the database
// already knows, with the archetype name as the emphasised word.
//
// A step whose date we can't read is dropped. An empty journey renders nothing at all.

import { byProminence, isSounding, type Archetype } from '../api/portrait';

export interface JourneyStep {
  id: string;
  /** "MARCH" — or "JUNE · NOW" for the last one. */
  when: string;
  /** The sentence, split so exactly one word carries the italic accent. */
  before: string;
  pivot: string;
  after: string;
  isNow: boolean;
}

function monthLabel(iso: string): string | null {
  const parsed = new Date(iso.includes('T') ? iso : `${iso.replace(' ', 'T')}Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, { month: 'long' }).toUpperCase();
}

/**
 * Up to three moments: the first archetypes to appear, then where the portrait stands now.
 *
 * Ordered oldest first so the timeline reads downward the way the design draws it, and capped at
 * three because a longer list stops being an arc and becomes a log.
 */
export function journeyFromArchetypes(archetypes: Archetype[], max = 3): JourneyStep[] {
  const dated = archetypes
    .filter((a) => a.status !== 'dormant' && isSounding(a) && a.firstDetectedAt)
    .map((a) => ({ archetype: a, when: monthLabel(a.firstDetectedAt!), at: a.firstDetectedAt! }))
    .filter((row): row is { archetype: Archetype; when: string; at: string } => row.when !== null)
    .sort((a, b) => a.at.localeCompare(b.at));

  if (dated.length === 0) return [];

  const lead = [...archetypes]
    .filter((a) => a.status !== 'dormant' && isSounding(a))
    .sort(byProminence)[0];

  const early = dated.slice(0, Math.max(1, max - 1));
  const steps: JourneyStep[] = early.map((row) => ({
    id: `first-${row.archetype.key}`,
    when: row.when,
    before: '',
    pivot: row.archetype.name,
    after: ` first showed up in what you were writing, across ${row.archetype.distinctEntryCount} ${row.archetype.distinctEntryCount === 1 ? 'entry' : 'entries'}.`,
    isNow: false,
  }));

  if (lead) {
    const now = monthLabel(lead.lastReinforcedAt || new Date().toISOString());
    steps.push({
      id: 'now',
      when: now ? `${now} · NOW` : 'NOW',
      before: 'The strongest thread right now is ',
      pivot: lead.name,
      after: `, across ${lead.distinctEntryCount} ${lead.distinctEntryCount === 1 ? 'entry' : 'entries'}.`,
      isNow: true,
    });
  }

  return steps.slice(-max);
}
